import { useEffect, useRef } from "react";
import { supabase } from "./supabase";

/**
 * Flata holder seg selv oppdatert.
 *
 * To veier inn, og begge trengs:
 *
 * REALTIME for det som skjer mens man ser på. Legger A-foto inn et bilde
 * mens produsenten har sida åpen, skal det dukke opp — ellers er det ikke
 * en felles plan, det er to kopier av en plan.
 *
 * OG EN HENTING NÅR FANEN BLIR SYNLIG IGJEN. En websocket som har ligget
 * nede mens maskinen sov sier ingenting om hva den gikk glipp av. Det er
 * den som redder de vanlige tilfellene: lokket igjen, lokket opp.
 *
 * SAMLER OPP. En omstokking skriver én rad per linje, og uten en liten
 * pause ville flata hentet alt på nytt for hver av dem.
 *
 * ⚠ `navn` MÅ VÆRE UNIKT PER FLATE. Pageren monterer alle sidene samtidig,
 * og `supabase.channel(navn)` gir samme kanal tilbake for samme navn — den
 * andre flata legger da til lyttere etter at den første har kalt
 * `subscribe()`, og realtime-klienten kaster. Det tar hele siden med seg, og
 * feilen peker ikke på navnet. Skjedde 24.10.2026, da reporterens synkflate
 * og redaktørens spørsmålsliste begge het «synk».
 */

const PAUSE_MS = 250;

export function useLevende(
  navn: string,
  tabeller: readonly string[],
  hentPaaNytt: () => void,
): void {
  /* Lest i lytterne, som ikke skal settes opp på nytt for hver tegning. */
  const hent = useRef(hentPaaNytt);
  hent.current = hentPaaNytt;

  const noekkel = tabeller.join(",");

  useEffect(() => {
    const timer = { id: 0 };
    const snart = (): void => {
      window.clearTimeout(timer.id);
      timer.id = window.setTimeout(() => hent.current(), PAUSE_MS);
    };

    const naarSynlig = (): void => {
      if (document.visibilityState === "visible") snart();
    };
    document.addEventListener("visibilitychange", naarSynlig);
    window.addEventListener("online", snart);

    if (supabase === null) {
      return () => {
        window.clearTimeout(timer.id);
        document.removeEventListener("visibilitychange", naarSynlig);
        window.removeEventListener("online", snart);
      };
    }

    const kanal = supabase.channel(`levende:${navn}`);
    for (const tabell of noekkel.split(",")) {
      kanal.on("postgres_changes", { event: "*", schema: "public", table: tabell }, snart);
    }
    kanal.subscribe();

    return () => {
      window.clearTimeout(timer.id);
      document.removeEventListener("visibilitychange", naarSynlig);
      window.removeEventListener("online", snart);
      void supabase?.removeChannel(kanal);
    };
  }, [navn, noekkel]);
}
